/* eslint-disable @typescript-eslint/no-invalid-void-type */
/**
 * Conversations thread write hooks (send/outcome/push/hot-override) + draft SSE.
 */
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import type { OutcomeStatus } from '@barel/core';

import { useApiClient } from '../context';
import { ApiError, getClientBaseUrl, getClientFetch, parseApiError } from '../hc';
import { postJson } from './_http';
import { conversationKey } from './_keys';
import { conversationMessagesQueryKey } from './inbox';

/* ─── Conversations thread — Wave 5c.2 (FULLY IMPLEMENTED) ──────────────── */

/**
 * Outcome status options for a pending action — matches the API contract:
 *   - `followup` enqueues a relance (Wave 6 wires the scheduler).
 *   - `archive`  archives the conversation.
 *   - `client`   pushes the contact into Leads (5d.2 finalises the
 *                leads-side insert).
 *   - `won`      marks the conv closed-won (archived) — Wave D5.
 *   - `lost`     marks the conv closed-lost (archived) — Wave D5.
 *   - `unknown`  records the outcome without archiving or scheduling a
 *                relance — Wave D5; the user revisits later.
 */
export type ConversationOutcomeStatus = OutcomeStatus;

export interface SendMessageInput {
  body: string;
  outboundSource?: 'ai_auto' | 'ai_review' | 'manual_app';
  clientToken?: string;
}

export interface SendMessageResponse {
  message: {
    id: string;
    conversationId: string;
    role: 'out';
    body: string;
    outboundSource: 'ai_auto' | 'ai_review' | 'manual_app';
  };
}

export interface OutcomeInput {
  status: ConversationOutcomeStatus;
  note?: string;
}

export interface OutcomeResponse {
  outcome: {
    conversationId: string;
    status: ConversationOutcomeStatus;
    at: string;
  };
  /** Present when the follow-up scheduler committed a queue row. */
  relanceScheduled?: { id: string; scheduledFor: string } | null;
  /** Machine-readable reason when the outcome saved but scheduling did not. */
  relanceScheduleError?: string | null;
}

export interface PushToLeadsInput {
  auto?: boolean;
}

export interface PushToLeadsResponse {
  pushedToLeads: {
    conversationId: string;
    at: string | null;
    auto?: boolean;
  };
}

export interface HotOverrideInput {
  enabled: boolean;
}

export interface HotOverrideResponse {
  hotOverride: {
    conversationId: string;
    enabled: boolean;
  };
}

// `conversationKey` is defined earlier (5c.3 helper) — reuse it here.
// `conversationMessagesKey` mirrors `conversationMessagesQueryKey` exported
// alongside `useConversationMessages` so write-side hooks invalidate the
// same cache that the read hook populates.
const conversationMessagesKey = (id: string) => conversationMessagesQueryKey(id);

/**
 * `POST /api/v1/conversations/:id/send` — sends the (already-edited) draft
 * or a manual reply. Optimistic by design: the bubble appears immediately
 * via the consumer's local state, then the server response confirms.
 *
 * On success we invalidate the conversation messages query (5c.1's
 * `useConversationMessages`) so a fresh fetch picks up the persisted row.
 */
export function useSendMessage(
  conversationId: string,
): UseMutationResult<SendMessageResponse, ApiError, SendMessageInput> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<SendMessageResponse, ApiError, SendMessageInput>({
    mutationFn: (vars) =>
      postJson<SendMessageResponse>(
        client,
        `/api/v1/conversations/${encodeURIComponent(conversationId)}/send`,
        vars,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: conversationMessagesKey(conversationId) });
      void qc.invalidateQueries({ queryKey: conversationKey(conversationId) });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

/**
 * `POST /api/v1/conversations/:id/outcome` — records a pending-action
 * outcome (US-076).
 *
 * Wave 6 chains `status === 'followup'` to the relances scheduler;
 * `status === 'archive'` archives the conversation; `status === 'client'`
 * pushes to Leads. The hook invalidates the conversation cache so the UI
 * re-renders the green confirmation strip + (optional) "remise dans le
 * flux de relances" callout.
 */
export function useOutcomeReporter(
  conversationId: string,
): UseMutationResult<OutcomeResponse, ApiError, OutcomeInput> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<OutcomeResponse, ApiError, OutcomeInput>({
    mutationFn: (vars) =>
      postJson<OutcomeResponse>(
        client,
        `/api/v1/conversations/${encodeURIComponent(conversationId)}/outcome`,
        vars,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: conversationKey(conversationId) });
      void qc.invalidateQueries({ queryKey: conversationMessagesKey(conversationId) });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

/**
 * `POST /api/v1/conversations/:id/push-to-leads` — flips the
 * `lead_pushed` flag and (Wave 5d.2) inserts the leads-side row.
 */
export function usePushToLeads(
  conversationId: string,
): UseMutationResult<PushToLeadsResponse, ApiError, PushToLeadsInput | void> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<PushToLeadsResponse, ApiError, PushToLeadsInput | void>({
    mutationFn: (vars) =>
      postJson<PushToLeadsResponse>(
        client,
        `/api/v1/conversations/${encodeURIComponent(conversationId)}/push-to-leads`,
        vars ?? {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: conversationKey(conversationId) });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

/**
 * `POST /api/v1/conversations/:id/hot-override` — toggles the
 * `is_very_hot` flag for the conversation (US-078).
 */
export function useHotOverride(
  conversationId: string,
): UseMutationResult<HotOverrideResponse, ApiError, HotOverrideInput> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<HotOverrideResponse, ApiError, HotOverrideInput>({
    mutationFn: (vars) =>
      postJson<HotOverrideResponse>(
        client,
        `/api/v1/conversations/${encodeURIComponent(conversationId)}/hot-override`,
        vars,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: conversationKey(conversationId) });
    },
  });
}

/* Backwards-compatible alias for the typed-stub name. */
export const useSetConversationOutcome = useOutcomeReporter;
export const usePushConversationToLeads = usePushToLeads;

/**
 * `POST /api/v1/conversations/:id/draft` — SSE consumer for AI draft tokens.
 * Imperative iterator factory so consumers can drive `useState` /
 * `useEffect` themselves. The stream yields incremental `delta` chunks
 * then a final `done: true` frame carrying the full assembled `body` and
 * the persisted `draftId`. Backed by `runDraftStream` (AI Gateway →
 * OpenRouter), so each `open()` produces a fresh LLM-generated draft.
 */
export interface DraftStreamFrame {
  delta?: string;
  done?: boolean;
  body?: string;
  draftId?: string;
  conversationId?: string;
  /**
   * SSE `event: error` frame — Wave D10 reconciles the shape with the HTTP
   * envelope produced by `apps/api/src/middleware/error.ts` so a single
   * client renderer handles both surfaces.
   */
  error?: {
    code: string;
    i18nKey: string;
    httpStatus: number;
    retryable: boolean;
    message: string;
    correlationId: string;
    details?: unknown;
  };
}

export function useDraft(): {
  open: (params: {
    conversationId: string;
    signal?: AbortSignal;
  }) => Promise<AsyncIterable<DraftStreamFrame>>;
} {
  const client = useApiClient();
  return {
    open: async ({ conversationId, signal }) => {
      const baseUrl = getClientBaseUrl(client);
      const wrappedFetch = getClientFetch(client);
      const response = await wrappedFetch(
        `${baseUrl}/api/v1/conversations/${encodeURIComponent(conversationId)}/draft`,
        {
          method: 'POST',
          headers: { accept: 'text/event-stream', 'content-type': 'application/json' },
          body: '{}',
          signal,
        },
      );
      if (!response.ok) {
        throw await parseApiError(response);
      }
      if (!response.body) {
        throw new ApiError({
          code: 'internal_error',
          httpStatus: 500,
          i18nKey: 'errors.internal_error',
          message: 'response body missing',
        });
      }
      return iterateDraftFrames(response.body, signal);
    },
  };
}

async function* iterateDraftFrames(
  body: ReadableStream<Uint8Array>,
  signal?: AbortSignal,
): AsyncIterable<DraftStreamFrame> {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  try {
    while (true) {
      if (signal?.aborted) break;
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let frameEnd = buffer.indexOf('\n\n');
      while (frameEnd !== -1) {
        const frame = buffer.slice(0, frameEnd);
        buffer = buffer.slice(frameEnd + 2);
        const parsed = parseDraftFrame(frame);
        if (parsed) yield parsed;
        frameEnd = buffer.indexOf('\n\n');
      }
    }
    if (buffer.length > 0) {
      const parsed = parseDraftFrame(buffer);
      if (parsed) yield parsed;
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* ignore */
    }
  }
}

function parseDraftFrame(frame: string): DraftStreamFrame | null {
  let data = '';
  for (const line of frame.split('\n')) {
    if (line.startsWith(':') || line.length === 0) continue;
    if (line.startsWith('data:')) data += line.slice(5).trim();
  }
  if (!data) return null;
  try {
    return JSON.parse(data) as DraftStreamFrame;
  } catch {
    return null;
  }
}
